import { Entity } from "./generics";
import { isObject } from "../utilities";
import { SchemaACL } from "./acl";
import { DistributedAlterations, DistributedEntity } from "./distributed-entity";
import {
  ALL_COUNTERS,
  ALL_DASHBOARDS,
  ALL_DATA_SOURCES,
  ALL_FIELDS,
  ALL_GROUPS,
  ALL_OBJECTS,
  ALL_PROJECT_CONFIGS,
  ALL_ROLES,
  ALL_SCHEDULES,
  ALL_TRAINING,
  ALL_VIDEO_ITEMS,
} from "../constants/firebase/constants";
import { IField, isField } from "./object/field";
import { IFieldGroup, IFieldGroupBuilt, isFieldGroup } from "./object/field-group";

// oxlint-disable-next-line no-shadow
export enum SchemaTypes {
  "counter" = "counter",
  "field" = "field",
  "group" = "group",
  "object" = "object",
  "project" = "project",
  "training" = "training",
  "notification" = "notification",
  "role" = "role",
  "videoItem" = "videoItem",
  "dashboard" = "dashboard",
  "schedule" = "schedule",
  "dataSource" = "dataSource",
}

export type RefType =
  | "counter"
  | "field"
  | "field-group"
  | "user"
  | "document"
  | "training"
  | "role"
  | "object"
  | "section"
  | "notification"
  | "videoItem"
  | "dashboard"
  | "job"
  | "schedule"
  | "data-source"
  | "project"
  | "data-source-instance";

export type SchemaRef = { type: RefType };
export type SchemaRefs = { [schemaId: string]: SchemaRef };

export type SchemaBuildRef = { version: number; type?: RefType };
export type SchemaBuildRefs = { [schemaId: string]: SchemaBuildRef };

export type SchemaBase<T = unknown, B = unknown> = {
  type: SchemaTypes;
  name: string;
  schema: T;
  version: number;
  savedVersions: number[];
  build?: B;

  /**
   * buildRefs are references to other schemas and or other entities which rely on the built schema.
   * In the case when other entries need to be referenced theres a type string that can be used to label the type. e.g., users
   *
   * e.g., for field here its referenced within a group
   *
   * {
   *  [field-guid]: {
   *    type: "field-group",
   *    version: 1
   *  }
   * }
   *
   */
  buildRefs: SchemaBuildRefs;
  buildVersion: number;
  builtAt?: number;

  /**
   * externalRefs are references to other schemas which reference this schema.
   * In the case when other entries need to be referenced theres a type string that can be used to label the type. e.g., users
   *
   * Current uses for external refs are deletion protection (cannot delete schema items that are used)
   *
   * e.g., for field-groups where a group may be used within a training
   *
   * {
   *  [training-guid]: {
   *    type: "training"
   *  }
   * }
   *
   *
   * e.g., for field here its referenced within a group
   *
   * {
   *  [field-guid]: {
   *    type: "field-group"
   *  }
   * }
   */
  externalRefs: SchemaRefs;
  externalRefIds: string[];
  internalRefs?: SchemaRefs;
  internalRefIds?: string[];

  system?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  isTemplate?: boolean;

  /**
   * Access Control Layer for schemas
   * - currently only implemented for Forms, Task, and Trainings
   */
  acl?: SchemaACL[];
} & DistributedEntity &
  DistributedAlterations;

export type Schema<T = unknown, B = unknown> = SchemaBase<T, B> & Entity;

export type SchemaReference<T = unknown, B = never> = SchemaBase<T, B>;

export type SchemasReferencesRecord<T, B = never> = Record<string, SchemaReference<T, B>>; // prettier-ignore

export function isFieldSchema(test: unknown): test is Schema<IField, undefined> {
  return isSchema(test) && isField(test.schema) && !test.build;
}

export function isFieldGroupSchema(test: unknown): test is Schema<IFieldGroup, IFieldGroupBuilt> {
  return isSchema(test) && isFieldGroup(test.schema) && isFieldGroup(test.build);
}

export function isSchema(test: unknown): test is Schema {
  return (
    typeof test === "object" &&
    Boolean(test) &&
    Object.getOwnPropertyNames(test).includes("type") &&
    Object.getOwnPropertyNames(test).includes("externalRefs") &&
    Object.getOwnPropertyNames(test).includes("version") &&
    Object.getOwnPropertyNames(test).includes("schema") &&
    Object.getOwnPropertyNames(test).includes("savedVersions")
  );
}

export function hasInternalRef(
  test: unknown,
): test is Pick<Schema, "internalRefIds" | "internalRefs"> {
  return (
    isObject(test) ||
    // oxlint-disable-next-line typescript/no-explicit-any
    isObject((test as any)?.internalRefs) ||
    // oxlint-disable-next-line typescript/no-explicit-any
    Array.isArray((test as any).internalRefIds)
  );
}

export function getSchemaTypeFromRefType(ref: RefType): SchemaTypes | undefined {
  switch (ref) {
    case "field-group":
      return SchemaTypes.group;
    case SchemaTypes.counter:
      return SchemaTypes.counter;
    case SchemaTypes.field:
      return SchemaTypes.field;
    case SchemaTypes.object:
      return SchemaTypes.object;
    case SchemaTypes.training:
      return SchemaTypes.training;
    case SchemaTypes.notification:
      return SchemaTypes.notification;
    case SchemaTypes.role:
      return SchemaTypes.role;
    case SchemaTypes.videoItem:
      return SchemaTypes.videoItem;
    case SchemaTypes.dashboard:
      return SchemaTypes.dashboard;
    case SchemaTypes.schedule:
      return SchemaTypes.schedule;
    case "data-source":
      return SchemaTypes.dataSource;
    default:
      return undefined;
  }
}

export function getRefTypeFromSchema(obj: Schema): RefType {
  switch (obj.type) {
    case SchemaTypes.group:
      return "field-group";
    case SchemaTypes.dataSource:
      return "data-source";
    case SchemaTypes.counter:
    case SchemaTypes.field:
    case SchemaTypes.object:
    case SchemaTypes.training:
    case SchemaTypes.notification:
    case SchemaTypes.role:
    case SchemaTypes.videoItem:
    case SchemaTypes.dashboard:
    case SchemaTypes.schedule:
    default:
      return obj.type;
  }
}

export const schemaTypeIndexMap: Record<SchemaTypes, string | undefined> = {
  [SchemaTypes.counter]: ALL_COUNTERS,
  [SchemaTypes.role]: ALL_ROLES,
  [SchemaTypes.field]: ALL_FIELDS,
  [SchemaTypes.group]: ALL_GROUPS,
  [SchemaTypes.object]: ALL_OBJECTS,
  [SchemaTypes.project]: ALL_PROJECT_CONFIGS,
  [SchemaTypes.training]: ALL_TRAINING,
  [SchemaTypes.notification]: undefined,
  [SchemaTypes.videoItem]: ALL_VIDEO_ITEMS,
  [SchemaTypes.dashboard]: ALL_DASHBOARDS,
  [SchemaTypes.schedule]: ALL_SCHEDULES,
  [SchemaTypes.dataSource]: ALL_DATA_SOURCES,
};
